// src/pages/MyPitches.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../lib/AuthContext";
import AppShell, { AppPageHeader } from "../components/AppShell";
import { format, formatDistanceToNow } from "date-fns";

const STATUS_LABELS = {
  pending: { label: "Under review", badge: "badge-gray" },
  pending_review: { label: "Under review", badge: "badge-gray" },
  approved: { label: "Approved", badge: "badge-green" },
  changes_requested: { label: "Changes requested", badge: "badge-purple" },
  shortlisted: { label: "Shortlisted", badge: "badge-purple" },
  matched: { label: "Matched", badge: "badge-green" },
  declined: { label: "Declined", badge: "badge-coral" },
};

function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatFeedbackReceived(value) {
  const date = toDate(value);
  if (!date) return null;
  return `Feedback received on ${format(date, "MMM d, yyyy")}`;
}

export default function MyPitches() {
  const { currentUser } = useAuth();
  const [pitches, setPitches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "pitches"), where("startupId", "==", currentUser.uid), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, snap => {
      setPitches(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [currentUser]);

  const stats = {
    total: pitches.length,
    shortlisted: pitches.filter(p => p.status === "shortlisted").length,
    matched: pitches.filter(p => p.status === "matched").length,
  };

  return (
    <AppShell>
      <AppPageHeader title="My solutions" subtitle="Track pitches you’ve submitted and any admin feedback." />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
          { label: "Total pitches", value: stats.total },
          { label: "Shortlisted", value: stats.shortlisted },
          { label: "Matched", value: stats.matched },
        ].map(s => (
          <div key={s.label} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: "18px 16px", textAlign: "left" }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: s.value > 0 ? "var(--teal)" : "var(--text-3)" }}>{s.value}</div>
            <div style={{ fontSize: 14, color: "var(--text-3)", marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {loading ? <div className="page-loading"><div className="spinner" style={{ width: 24, height: 24 }}></div></div>
        : pitches.length === 0 ? (
          <div className="empty-state card" style={{ padding: "48px 24px" }}>
            <h3>No solutions submitted yet</h3>
            <p>Browse the challenges feed and submit your first solution to a corporation.</p>
            <Link to="/feed" className="btn btn-primary" style={{ marginTop: 16 }}>Browse challenges →</Link>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {pitches.map(pitch => {
              const s = STATUS_LABELS[pitch.status] || STATUS_LABELS.pending;
              const hasFeedback = Boolean(pitch.adminFeedback?.trim());
              const feedbackDateLabel = formatFeedbackReceived(pitch.feedbackUpdatedAt);
              const summary = pitch.summary || "";
              const summaryPreview = summary.length > 140 ? `${summary.slice(0, 140)}…` : summary;

              return (
                <div key={pitch.id} className="card" style={{
                  padding: "16px 18px",
                  borderLeft: pitch.status === "matched" ? "3px solid var(--green)"
                    : hasFeedback ? "3px solid var(--teal)"
                    : pitch.status === "shortlisted" ? "3px solid var(--purple)"
                    : "1px solid var(--border)"
                }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                        <span className={`badge ${s.badge}`}>{s.label}</span>
                        {hasFeedback && (
                          <span className="admin-feedback-badge">Admin feedback</span>
                        )}
                        <span style={{ fontSize: 12, color: "var(--text-3)" }}>
                          {pitch.createdAt?.toDate ? formatDistanceToNow(pitch.createdAt.toDate(), { addSuffix: true }) : ""}
                        </span>
                      </div>
                      <div className="need-card-title" style={{ marginBottom: 4 }}>{pitch.needTitle}</div>
                      <div className="need-card-industry" style={{ marginBottom: 8 }}>{pitch.corpName}</div>
                      {summaryPreview && (
                        <p className="need-card-body" style={{ margin: 0 }}>{summaryPreview}</p>
                      )}
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      <Link to={`/need/${pitch.needId}`} className="btn btn-outline" style={{ fontSize: 14, padding: "8px 14px", whiteSpace: "nowrap", minHeight: 40 }}>
                        View challenge
                      </Link>
                    </div>
                  </div>

                  {hasFeedback && (
                    <div className="startup-feedback-callout" role="note" aria-label="Admin feedback">
                      <div className="startup-feedback-callout-header">
                        <span className="startup-feedback-callout-title">Admin Feedback</span>
                        {feedbackDateLabel && (
                          <span className="startup-feedback-date-badge">{feedbackDateLabel}</span>
                        )}
                      </div>
                      <p className="startup-feedback-callout-body">{pitch.adminFeedback}</p>
                    </div>
                  )}

                  {pitch.status === "matched" && (
                    <div style={{
                      marginTop: 12, padding: "10px 14px",
                      background: "var(--green-light)", borderRadius: "var(--radius)",
                      fontSize: 13, color: "var(--green-text)"
                    }}>
                      You've been matched! {pitch.corpName} will be in touch at your registered email.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </AppShell>
  );
}
